import React, { Component } from 'react';
import moment from 'moment';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { DataTable, ToneChip } from '../../../../ui';
import { colors } from '../../../../theme/tokens';
import actions from '../../../../redux/actions';
import { OperationStatus } from '../../../../types/OperationStatus';

const mapStateToProps = (state) => {
  return {
    user: state.user,
    finance: state.userFinance,
    withdrawalStatus: state.withdrawalStatus,
    listOwnFinanceStatus: state.listOwnFinanceStatus,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    updateUser: (data) => dispatch(actions.updateUser(data)),
    listOwnFinance: () => dispatch(actions.listOwnFinance()),
    saveUser: (data: any, notify?: boolean) => dispatch(actions.saveUser(data, notify)),
    withdraw: () => dispatch(actions.withdraw()),
  };
};

class Finance extends Component<any> {
  public state: any;

  constructor(props) {
    super(props);

    this.state = {
      errors: new Set(),
    };
  }

  resetErrorState = (event) => {
    const name = event.target.name;
    const errors = this.state.errors;

    if (errors.has(name)) {
      errors.delete(name);
      this.setState({ errors });
    }
  };

  onChange = (event, data) => {
    const user = {};

    if (data.name !== 'paypal') {
      return;
    }

    user[data.name] = data.value;

    this.props.updateUser(user);
    this.props.saveUser(user, false); // TODO fix later
  };

  onWithdraw = (event, data) => {
    if (!this.props.user.paypal || !this.props.user.paypal.length) {
      return this.setState({
        errors: new Set(['paypal']),
      });
    }

    this.props.withdraw();
  };

  componentDidMount() {
    this.props.listOwnFinance();
  }

  render() {
    const finance = this.props.finance || []; // TODO rename finance -> cycleSummaries

    const availableForWithdrawalGross = finance.reduce((result, el) => {
      if (!el.paidAt && moment().isSameOrAfter(el.withdrawalAvailableFrom)) {
        result += el.amountToPay;
      }
      return result;
    }, 0);

    const paypalComission =
      (availableForWithdrawalGross / 100) * 2 > 1 ? 1 : (availableForWithdrawalGross / 100) * 2;

    const availableForWithdrawalNet = availableForWithdrawalGross - paypalComission;

    const pendingTotal = finance.reduce((result, el) => {
      if (!el.paidAt && moment().isBefore(el.withdrawalAvailableFrom)) {
        result += el.amountToPay;
      }
      return result;
    }, 0);

    const withdrewTotal = finance.reduce((result, el) => {
      if (el.paidAt) {
        result += el.amountToPay;
      }
      return result;
    }, 0);

    const withdrawProcessing = this.props.withdrawalStatus.status === OperationStatus.processing;
    const withdrawDisabled =
      availableForWithdrawalNet <= 0 ||
      withdrawProcessing ||
      this.props.listOwnFinanceStatus.status === OperationStatus.processing;

    const money = (amount: number) => `$${Number(amount || 0).toFixed(2)}`;

    const stat = (label: string, value: string, hint?: string) => (
      <Box sx={{ border: `1px solid ${colors.dividerBlue}`, borderRadius: 2, p: 2, flex: 1, minWidth: 180 }}>
        <Typography sx={{ fontSize: 12, color: colors.textGrey, display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {label}
          {hint && (
            <Tooltip title={hint}>
              <InfoOutlinedIcon sx={{ fontSize: 15 }} />
            </Tooltip>
          )}
        </Typography>
        <Typography sx={{ fontSize: 22, fontWeight: 600, color: colors.titleBlue }}>{value}</Typography>
      </Box>
    );

    const status = (row) => {
      if (row.paidAt) {
        return <ToneChip label={`Paid ${moment(row.paidAt).format('DD.MM.YYYY')}`} tone="neutral" />;
      }
      if (moment().isBefore(moment(row.withdrawalAvailableFrom))) {
        return <ToneChip label={`Available ${moment(row.withdrawalAvailableFrom).format('DD.MM.YYYY')}`} tone="warning" />;
      }
      return <ToneChip label="Available for withdrawal" tone="success" />;
    };

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <TextField
            name="paypal"
            label="Your PayPal account"
            value={this.props.user.paypal || ''}
            error={this.state.errors.has('paypal')}
            helperText={this.state.errors.has('paypal') ? 'Enter your PayPal account to withdraw' : 'Saved when you leave the field'}
            FormHelperTextProps={{ sx: { color: this.state.errors.has('paypal') ? 'error.main' : 'text.secondary' } }}
            onChange={(event) => this.props.updateUser({ paypal: event.target.value })}
            onFocus={() => this.resetErrorState({ target: { name: 'paypal' } })}
            onBlur={(event) => this.onChange(null, { name: 'paypal', value: event.target.value })}
            sx={{ minWidth: 300 }}
          />
          <Button variant="contained" size="large" disabled={withdrawDisabled} onClick={(event) => this.onWithdraw(event, null)} sx={{ mt: 0.5 }}>
            {withdrawProcessing ? 'Withdrawing…' : 'Withdraw with PayPal'}
          </Button>
        </Box>

        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          {stat('Available for withdrawal', money(availableForWithdrawalNet), 'PayPal commission of 2% of your total available credit was deducted')}
          {stat(
            'Pending credit',
            money(pendingTotal),
            "Credits are available to withdraw for unpaid cycles that ended before the 15th of the previous month. Cycles that completed after the last 15th are pending and become available after the 15th of this month."
          )}
          {stat('Withdrawn so far', money(withdrewTotal))}
        </Box>

        <Box>
          <Typography component="h3" sx={{ fontSize: 15, fontWeight: 600, color: colors.titleBlue, mb: 1 }}>Credit summary</Typography>
          <DataTable
            rows={finance}
            rowKey={(row: any, i) => `${row.cycleId || row.title}-${i}`}
            pageSize={20}
            emptyText="No credits yet"
            columns={[
              { key: 'title', label: 'Project', render: (row: any) => row.title },
              { key: 'createdAt', label: 'Accepted on', render: (row: any) => moment(row.createdAt).format('DD.MM.YYYY') },
              { key: 'testerBid', label: 'My bid', align: 'right', render: (row: any) => money(row.testerBid) },
              { key: 'hours', label: 'Hours', align: 'right', render: (row: any) => row.hoursPerTester },
              { key: 'amountToPay', label: 'Amount', align: 'right', render: (row: any) => money(row.amountToPay) },
              { key: 'status', label: 'Status', render: status },
            ]}
          />
        </Box>

        <Typography variant="body2" color="textSecondary">
          Have any questions? <Link href="/contact">Contact us</Link>
        </Typography>
      </Box>
    );
  }
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(Finance));
